import { ChevronLeft } from 'components/icons';
import { Button } from 'components/ui/button';
import { useId } from 'react';
import { InvoiceData } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { InvoiceBillingNotesCard } from './InvoiceBillingNotesCard';
import { InvoiceDocumentsCard } from './InvoiceDocumentsCard';
import { InvoiceSummaryCard } from './InvoiceSummaryCard';

/** The grid's columns while the side column is collapsed, from `md` where it stands beside the cards. */
export const SIDE_COLLAPSED_GRID_CLASS = 'md:grid-cols-[minmax(0,1fr)_0] min-[1280px]:grid-cols-[minmax(0,1fr)_0]';

interface Props {
    invoice: InvoiceData;
    isLoading: boolean;
    templateValidation: () => Promise<boolean>;
    /** The page's view mode, which keeps the rounding matrix read-only. */
    isInViewMode: boolean;
    isOpen: boolean;
    onToggle: () => void;
}

/**
 * The column beside every tab, outside the tab panel, so it stays mounted while the tabs switch: Summary, Documents
 * and Billing notes, each at its natural height, scrolling with the page. From `md`, where it stands beside the cards,
 * a round button on its edge collapses it to nothing and back, as classic's Summary drawer does; collapsed, its cards
 * stay mounted, hidden.
 */
export function InvoiceSideColumn({ invoice, isLoading, templateValidation, isInViewMode, isOpen, onToggle }: Props) {
    const cardsId = useId();
    return (
        <div className="relative min-w-0">
            {/* Centred in the gap before the column, on the line of the first card's title. */}
            <Button
                variant="outline"
                size="icon-sm"
                aria-label={t('invoices.summary-sidebar.title')}
                aria-expanded={isOpen}
                aria-controls={cardsId}
                onClick={onToggle}
                className="bg-card hover:bg-muted text-muted-foreground absolute start-[calc(var(--card-gap)*-0.5)] top-[17px] z-10 size-7 -translate-x-1/2 rounded-full shadow-xs max-md:hidden rtl:translate-x-1/2"
            >
                <ChevronLeft
                    aria-hidden
                    className={cn('size-4 transition-transform motion-reduce:transition-none', isOpen && 'rotate-180')}
                />
            </Button>
            <div id={cardsId} className={cn('flex min-w-0 flex-col gap-(--card-gap)', !isOpen && 'md:hidden')}>
                <InvoiceSummaryCard invoice={invoice} isInViewMode={isInViewMode} />
                <InvoiceDocumentsCard invoice={invoice} isLoading={isLoading} templateValidation={templateValidation} />
                <InvoiceBillingNotesCard invoice={invoice} />
            </div>
        </div>
    );
}
